import React from 'react';

/**
 * A small, self-contained error boundary for embedded views (e.g. the Log
 * Center tabs). The app only has a single ROOT Bugsnag boundary, so before this
 * a render error in any tab white-screened the whole route. This boundary
 * CONTAINS the crash to the wrapped subtree and renders the actual error message
 * in-place — the surrounding page chrome + sibling tabs stay usable, and the
 * cause is visible without opening devtools.
 *
 * Pass a `resetKey` (e.g. the active tab key) — when it changes the boundary
 * clears its error, so switching tabs (or retrying) re-attempts the render.
 */
interface Props {
  children: React.ReactNode;
  /** Changing this value resets the boundary (e.g. the active tab). */
  resetKey?: string | number;
  /** Short, human label for the fallback heading. */
  label?: string;
}

interface State {
  error: Error | null;
}

class TabErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prevProps: Props): void {
    // Reset when the caller signals a context change (e.g. tab switch) so a
    // previously-crashed view gets a fresh attempt.
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Keep the console trail (and let the root Bugsnag boundary's listeners see
    // it too) without re-throwing.
    // eslint-disable-next-line no-console
    console.error('[TabErrorBoundary] caught render error:', error, info.componentStack);
  }

  private handleRetry = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <div
        role='alert'
        style={{
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid var(--border-color, #e5e7eb)',
          background: 'var(--card-background-color, #fff)',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '8px' }}>
          {this.props.label ?? 'This view ran into an error and could not load.'}
        </div>
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: '0 0 12px',
            fontSize: '13px',
            lineHeight: 1.5,
            color: 'var(--error-color, #c0392b)',
          }}
        >
          {error.message || String(error)}
        </pre>
        <button
          type='button'
          onClick={this.handleRetry}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid var(--border-color, #e5e7eb)',
            background: 'transparent',
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}

export default TabErrorBoundary;
